// The portal's one MUI theme (L2). Provided once in App.js through
// @mui/material/styles ThemeProvider, which also feeds @mui/styles makeStyles
// and withStyles consumers. Views should not create their own themes.
//
// Buttons (owner pick, option C): variant="contained" (primary) is the deeper
// orange #C73D0E with a white label; variant="outlined" color="neutral" is the
// grey "Secondary" kind (Cancel, Later); variant="text" is the "Quiet" kind;
// color="error" only for destructive actions (outlined, or contained for the
// last Yes of a confirm); color="success" variant="contained" is the green
// "go" kind (Accept, Start trip, Find driver, Add customer, Call driver).

import { createTheme } from '@mui/material/styles';
import { tokens as c, layout, tones } from './tokens';
import { FONT_STACK } from './fonts';

const COARSE = '@media (pointer: coarse)';

const standardAlert = (tone) => ({
  color: tone.fg,
  backgroundColor: tone.bg,
  '& .MuiAlert-icon': { color: tone.fg },
});

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: c.BUTTON_PRIMARY, light: c.ACCENT, dark: c.BUTTON_PRIMARY_PRESSED, contrastText: c.WHITE },
    secondary: { main: c.INK_2, light: c.TEXT_3, dark: c.INK, contrastText: c.WHITE },
    // Custom colours (use with color="brand" / color="neutral" or theme.palette.brand.main).
    brand: { main: c.ACCENT, light: c.ACCENT_TINT, dark: c.ACCENT_TEXT, contrastText: c.WHITE },
    neutral: { main: c.INK, light: c.SURFACE_ALT, dark: '#111827', contrastText: c.WHITE },
    error: { main: c.DANGER_TEXT, dark: '#991B1B', contrastText: c.WHITE },
    warning: { main: c.WARNING_TEXT, dark: c.AMBER_TEXT, contrastText: c.WHITE },
    info: { main: c.INFO_TEXT, dark: '#1E40AF', contrastText: c.WHITE },
    success: { main: c.SUCCESS, dark: c.SUCCESS_PRESSED, contrastText: c.WHITE },
    text: { primary: c.INK, secondary: c.INK_2, disabled: c.PLACEHOLDER_TEXT },
    divider: c.OUTLINE,
    background: { default: c.SCREEN_BG, paper: c.SURFACE },
  },
  shape: { borderRadius: layout.RADIUS_CONTROL },
  typography: {
    fontFamily: FONT_STACK,
    htmlFontSize: 16,
    fontSize: 14,
    // No Light weight anywhere: 300 renders as 400.
    fontWeightLight: 400,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    // Every line height is at least 1.35 so Amharic and Tigrinya never clip.
    h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: 0 },
    h2: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: 0 },
    h3: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: 0 },
    h4: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: 0 },
    h5: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.4, letterSpacing: 0 },
    h6: { fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.4, letterSpacing: 0 },
    subtitle1: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: 0 },
    subtitle2: { fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5, letterSpacing: 0 },
    body1: { fontSize: '1rem', lineHeight: 1.5, letterSpacing: 0 },
    body2: { fontSize: '0.875rem', lineHeight: 1.5, letterSpacing: 0 },
    button: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.4, letterSpacing: 0, textTransform: 'none' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, letterSpacing: 0 },
    overline: { fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.02em', textTransform: 'none' },
    // <Typography variant="numeral"> for KPI numbers and money totals.
    numeral: { fontFamily: FONT_STACK, fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: layout.CONTROL_HEIGHT,
          padding: '8px 16px',
          borderRadius: layout.RADIUS_CONTROL,
          [COARSE]: { minHeight: layout.TOUCH_TARGET },
        },
        sizeSmall: { minHeight: 32, padding: '4px 10px', fontSize: '0.875rem', [COARSE]: { minHeight: layout.TOUCH_TARGET } },
        sizeLarge: { minHeight: 48, padding: '10px 22px', fontSize: '1rem' },
        outlined: { borderWidth: 1.5, '&:hover': { borderWidth: 1.5 } },
        containedPrimary: {
          '&:hover': { backgroundColor: c.BUTTON_PRIMARY_PRESSED },
          '&.Mui-disabled': { backgroundColor: c.BUTTON_PRIMARY, color: c.WHITE, opacity: 0.45 },
        },
        containedSuccess: {
          '&:hover': { backgroundColor: c.SUCCESS_PRESSED },
          '&.Mui-disabled': { backgroundColor: c.SUCCESS, color: c.WHITE, opacity: 0.45 },
        },
        outlinedPrimary: { borderColor: c.BUTTON_PRIMARY, '&:hover': { borderColor: c.BUTTON_PRIMARY, backgroundColor: c.ACCENT_TINT } },
        outlinedError: { borderColor: c.DANGER, '&:hover': { borderColor: c.DANGER, backgroundColor: c.DANGER_TINT } },
        textPrimary: { '&:hover': { backgroundColor: c.ACCENT_TINT } },
      },
      variants: [
        // "Secondary" kind: grey outline, ink label.
        {
          props: { variant: 'outlined', color: 'neutral' },
          style: {
            color: c.INK,
            borderColor: c.OUTLINE_STRONG,
            '&:hover': { borderColor: c.OUTLINE_STRONG, backgroundColor: c.SURFACE_ALT },
          },
        },
        { props: { variant: 'text', color: 'neutral' }, style: { color: c.INK, '&:hover': { backgroundColor: c.SURFACE_ALT } } },
      ],
    },
    MuiIconButton: {
      styleOverrides: {
        // 44-px targets on touch screens, except an Autocomplete's clear and
        // open arrows: their box is placed for 28-px buttons and would slip
        // out of line (country picker, address and people search).
        root: {
          [COARSE]: {
            '&:not(.MuiAutocomplete-clearIndicator):not(.MuiAutocomplete-popupIndicator)': {
              minWidth: layout.TOUCH_TARGET,
              minHeight: layout.TOUCH_TARGET,
            },
          },
        },
      },
    },
    MuiPaper: {
      // One flat surface: white, 12-px corners, a 1-px line instead of a
      // shadow. Menus, popovers and dialogs keep their own elevation.
      defaultProps: { elevation: 0 },
      styleOverrides: {
        rounded: { borderRadius: layout.RADIUS_CARD },
        elevation0: { border: `1px solid ${c.OUTLINE}` },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { boxShadow: '0 6px 16px rgba(15,23,42,0.18)' },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem', minHeight: 44 },
      },
    },
    MuiTabs: {
      // The selected tab as the approved portal boards draw it: its words in
      // primary (#C73D0E, readable), a 2-px underline in the brand orange
      // #F1501A (W2-contrast C keeps #F1501A for selected marks).
      styleOverrides: {
        indicator: ({ ownerState }) => ({
          ...(ownerState && ownerState.vertical ? {} : { height: 2 }),
          ...(!ownerState || ownerState.indicatorColor === 'primary' ? { backgroundColor: c.ACCENT } : {}),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: c.INK_2 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: c.SURFACE,
          '&.Mui-disabled': { backgroundColor: c.SURFACE_ALT },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.8125rem', lineHeight: 1.45 } },
    },
    MuiMenuItem: {
      styleOverrides: { root: { [COARSE]: { minHeight: layout.TOUCH_TARGET } } },
    },
    MuiListItemButton: {
      styleOverrides: { root: { [COARSE]: { minHeight: layout.TOUCH_TARGET } } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: c.OUTLINE },
        head: { fontWeight: 600, color: c.INK_2 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.4, padding: '20px 24px 8px' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px 20px' } },
    },
    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'center' } },
    },
    MuiTooltip: {
      defaultProps: { enterTouchDelay: 0, leaveTouchDelay: 3000, arrow: true },
      styleOverrides: {
        tooltip: { backgroundColor: c.INK, fontSize: '0.8125rem', lineHeight: 1.45, padding: '6px 10px', borderRadius: 8 },
        arrow: { color: c.INK },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { fontWeight: 500 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: layout.RADIUS_CARD, border: 0, fontSize: '0.875rem', lineHeight: 1.5 },
        standardSuccess: standardAlert(tones.success),
        standardError: standardAlert(tones.error),
        standardWarning: standardAlert(tones.warning),
        standardInfo: standardAlert(tones.info),
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { root: { backgroundColor: c.SKELETON } },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: c.SCREEN_BG, color: c.INK },
      },
    },
  },
  // Extra keys for views: theme.tokens.ACCENT, theme.layout.APP_BAR_HEIGHT, theme.tones.success.fg
  tokens: c,
  layout,
  tones,
});

export default theme;
